<template>
  <section class="share-bar" aria-label="Share this page">
    <div class="share-inner">
      <div class="share-row">
        <div class="share-intro">
          <span class="share-icon"><Icon name="share" :size="20" color="#0f7a7a"></Icon></span>
          <div>
            <strong class="share-title">{{ phone ? "Know someone with old tapes?" : "Know someone with a box of old tapes?" }}</strong><br />
            <span class="share-sub">Share this page with family and friends.</span>
          </div>
        </div>

        <!-- Phones with a share sheet: one button -->
        <button
          v-if="phone && canNativeShare"
          type="button"
          class="t2d-btn t2d-btn--teal-outline t2d-btn--block"
          data-share="native"
          @click="nativeShare"
        >
          <Icon name="share" :size="18"></Icon>Share this page
        </button>

        <div v-else class="share-buttons">
          <a
            v-for="b in buttons"
            :key="b.platform"
            :href="b.href"
            :data-share="b.platform"
            :aria-label="`Share on ${b.label}`"
            class="share-btn"
            :style="{ background: b.bg, color: b.fg }"
            target="_blank"
            rel="noopener"
            @click="track(b.platform)"
          >
            <Icon v-if="b.icon" :name="b.icon" :size="18" :color="b.fg"></Icon>
            <Icon v-else name="mail" :size="18" :color="b.fg"></Icon>
            {{ b.label }}
          </a>
          <button type="button" data-share="copy" class="share-btn share-copy" @click="copy">
            <Icon :name="copied ? 'check' : 'copy'" :size="18"></Icon>{{ copied ? "Link copied" : "Copy link" }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Icon from "@/components/shell/Icon.vue";
import { market } from "@/utils/market";
import { track } from "@/utils/tracking";

const SHARE_TEXT = "Got old tapes at home? Tapes To Digital converts them locally — originals always returned.";

// "Share this page" bar above the footer. Shares the page's own URL (path
// only — no click IDs or UTMs), except on personal pages (/r/..., /manage/...,
// the thank-you page), which carry the customer's token: those share the
// market's homepage instead.
export default {
  name: "ShareBar",
  components: { Icon },
  data: () => ({
    copied: false,
    canNativeShare: typeof navigator !== "undefined" && typeof navigator.share === "function",
  }),
  computed: {
    phone() {
      return this.$vuetify.breakpoint.xsOnly;
    },
    shareUrl() {
      if (this.$route.matched.some((r) => r.meta && r.meta.personal)) return `${market.website}/`;
      return `${window.location.origin}${this.$route.path}`;
    },
    buttons() {
      const u = encodeURIComponent(this.shareUrl);
      const t = encodeURIComponent(SHARE_TEXT);
      return [
        { platform: "facebook", label: "Facebook", icon: "facebook", bg: "#1877F2", fg: "#ffffff",
          href: `https://www.facebook.com/sharer/sharer.php?u=${u}` },
        { platform: "whatsapp", label: "WhatsApp", icon: "whatsapp", bg: "#25D366", fg: "#0b141a",
          href: `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${this.shareUrl}`)}` },
        // Messenger's share link only opens where the Messenger app is installed
        { platform: "messenger", label: "Messenger", icon: "messenger", bg: "#0866FF", fg: "#ffffff",
          href: `fb-messenger://share/?link=${u}` },
        { platform: "x", label: "X", icon: "x", bg: "#000000", fg: "#ffffff",
          href: `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
        { platform: "linkedin", label: "LinkedIn", icon: "linkedin", bg: "#0A66C2", fg: "#ffffff",
          href: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
        { platform: "email", label: "Email", icon: null, bg: "#4a5057", fg: "#ffffff",
          href: `mailto:?subject=${encodeURIComponent("Tapes To Digital")}&body=${encodeURIComponent(`${SHARE_TEXT}\n\n${this.shareUrl}`)}` },
      ];
    },
  },
  methods: {
    track(platform) {
      track({ event: "share_click", platform });
    },
    async nativeShare() {
      this.track("native");
      try {
        await navigator.share({ title: "Tapes To Digital", text: SHARE_TEXT, url: this.shareUrl });
      } catch (e) {
        // Closed the share sheet
      }
    },
    async copy() {
      this.track("copy");
      try {
        await navigator.clipboard.writeText(this.shareUrl);
      } catch (e) {
        // Older browsers / insecure context
        const input = document.createElement("textarea");
        input.value = this.shareUrl;
        document.body.appendChild(input);
        input.select();
        try {
          document.execCommand("copy");
        } catch (err) {
          // nothing more we can do
        }
        document.body.removeChild(input);
      }
      this.copied = true;
      setTimeout(() => (this.copied = false), 2500);
    },
  },
};
</script>

<style scoped>
.share-bar {
  background: #fff;
}
.share-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 32px 24px;
}
.share-row {
  border-top: 1px solid #e3e7ea;
  border-bottom: 1px solid #e3e7ea;
  padding: 24px 0;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}
.share-intro {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1 1 280px;
}
.share-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #e9f6f6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.share-title {
  font-size: 17px;
}
.share-sub {
  font-size: 14px;
  color: #4a5057;
}
.share-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.share-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border-radius: 999px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 700;
  border: 0;
  cursor: pointer;
}
.share-copy {
  border: 1.5px solid #cfd5da;
  background: #fff;
  color: #1e1e1e;
}
@media (max-width: 599px) {
  .share-inner {
    padding: 24px 16px;
  }
  .share-row {
    padding: 20px 0;
    gap: 12px;
  }
  .share-title {
    font-size: 16px;
  }
  .share-sub {
    font-size: 13px;
  }
}
</style>
